// ---------------------------------------------------------------------------
// useDrawerDetailsV3 — hook consolidado de carga do InputDrawerV3.
// ---------------------------------------------------------------------------
// Substitui a serie de hooks/fetches que cada pane disparava antes
// (``useInputNotesV3``, ``useInputDecisionsV3``, history, cross-links,
// reuse-config) por uma unica chamada ao endpoint consolidado
// ``GET /api/v3/inputs/<app>/<y>/<m>/<meta>/drawer-details/``.
//
// Objetivo (PA-007): cortar round-trips ao banco. O backend monta o
// payload em <=4 queries SQL (uma para o ``ImplementationInputV3``
// + responsibles, outra para os controls do mes corrente + anterior,
// outra para notes/decisions, outra para cross-links/audit). Aqui no
// front, o drawer abre com 1 fetch e mantem 1 polling enquanto o
// pipeline esta rodando.
//
// Comportamento:
//   - ``loading`` so e ``true`` na primeira carga (mudanca de
//     identidade do input). Re-fetches em polling NAO re-disparam
//     spinner — a UI continua viva sem flicker.
//   - Polling 5s enquanto ``control.state in {uploaded, processing}``.
//     Em estados terminais (``success``/``warning``/``error``/
//     ``system_error``) o polling para sozinho.
//   - Borda ``isUploading: true -> false`` dispara um fetch
//     imediato em background, com a mesma protecao de "stale" do
//     ``useInputNotesV3`` antigo: se o backend ainda nao registrou
//     o novo arquivo, mantemos o optimistic ``state="uploaded"``.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";

import { fetchInputDrawerDetailsV3 } from "../../../../services/inputsV3Service";

// Onda 1.9: alinhado com o polling do cockpit (4s em estados ativos).
// Antes 5s — o drawer ficava UM tick atras do snapshot principal,
// o que causava o NowPane a renderizar "aguardando para iniciar
// leitura" depois que a UI principal ja tinha avancado para
// ``cross-linking running``. Agora ambos pollings convergem na mesma
// cadencia e o usuario nao percebe descompasso entre as 2 superficies.
const POLL_INTERVAL_MS = 4000;

const TERMINAL_STATES = new Set([
  "success",
  "warning",
  "error",
  "system_error",
]);

const EMPTY = {
  loading: false,
  about: null,
  control: null,
  currentMonthUploads: [],
  previousMonthUpload: null,
  notes: [],
  answeredDecisions: [],
  decisions: [],
  crossLinks: [],
  auditRecent: [],
  runtimeInput: null,
  monthStatus: null,
  error: null,
};

function shapeFromPayload(data) {
  const runtimeInput = data?.runtime_input
    ? {
        ...data.runtime_input,
        meta_name:
          data.runtime_input.meta_name || data?.about?.meta_name || null,
      }
    : null;
  return {
    loading: false,
    about: data?.about || null,
    control: data?.control || null,
    currentMonthUploads: data?.current_month_uploads || [],
    previousMonthUpload: data?.previous_month_upload || null,
    notes: data?.notes || [],
    answeredDecisions: data?.answered_decisions || [],
    decisions: data?.decisions || [],
    crossLinks: data?.cross_links || [],
    auditRecent: data?.audit_recent || [],
    runtimeInput,
    monthStatus: data?.month_status || null,
    error: null,
  };
}

export function useDrawerDetailsV3({
  appName,
  year,
  month,
  metaName,
  isUploading,
  enabled = true,
}) {
  const canFetch = !!(enabled && appName && year && month && metaName);

  // Identidade do drawer (mai/2026): a tripla
  // ``(appName, year, month, metaName)`` define qual input o drawer
  // esta exibindo. Quando QUALQUER dimensao muda, o payload anterior
  // (notes/crossLinks/decisions) e' irrelevante e nao pode ser
  // mostrado nem por um frame — caso contrario o usuario que clica
  // no input B vindo do input A enxerga os crossLinks de A piscando
  // antes do fetch de B chegar. Sintoma reportado: drawer de
  // ``ajustes_venta_y_descuentos`` mostrando 25 xlinks de
  // ``venta_bruta`` (input anterior) ate o re-fetch.
  const identityKey = canFetch
    ? `${appName}|${year}|${month}|${metaName}`
    : null;

  // ``state`` + ``storedIdentity`` formam o par canonico do React 18
  // para "resetar state quando prop muda" sem effect — o reset
  // acontece SINCRONO durante o render onde ``identityKey`` muda,
  // entao os consumidores que leem ``crossLinks`` nesse mesmo
  // render ja recebem ``[]`` (em vez do array do input anterior).
  // Ref: react.dev/learn/you-might-not-need-an-effect (secao
  // "Adjusting some state when a prop changes").
  const [state, setState] = useState(EMPTY);
  const [storedIdentity, setStoredIdentity] = useState(identityKey);
  if (storedIdentity !== identityKey) {
    setStoredIdentity(identityKey);
    setState({ ...EMPTY, loading: !!identityKey });
  }

  // I-22 (auditoria mai/2026 — C8 do audit frontend): inicializar
  // com o valor ATUAL de ``isUploading`` em vez de ``false``. Antes,
  // se o drawer abria com upload ja em andamento (cenario: clica
  // upload na lista, depois abre o drawer durante os ~5-30s do
  // pipeline), o effect (2) detectava borda falsa FALSE→TRUE no
  // mount e limpava notes/crossLinks/decisions imediatamente —
  // apagando os dados que o effect (1) acabou de buscar do backend.
  // Inicializar com o valor atual elimina a borda falsa: se o
  // drawer abre com isUploading=true, ``wasUploading=true`` no
  // primeiro render, ``wasUploading && isUploading`` cai no branch
  // "ja em uploading" sem limpar nada novo.
  const prevUploadingRef = useRef(isUploading);
  const deliveredBeforeUploadRef = useRef(null);
  const controlRef = useRef(null);
  useEffect(() => {
    controlRef.current = state.control;
  }, [state.control]);

  // (1) Identidade do input mudou -> primeira carga.
  //
  // O reset do state ja' aconteceu sincrono no render acima (par
  // ``storedIdentity``/``setStoredIdentity``); aqui so disparamos o
  // fetch. Mantemos o effect porque side effects (network I/O) NAO
  // devem rodar durante o render — apenas reads/writes de state
  // sincronos sao seguros la.
  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    fetchInputDrawerDetailsV3({ appName, year, month, metaName })
      .then((data) => {
        if (cancelled) return;
        setState(shapeFromPayload(data));
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ ...EMPTY, loading: false, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch, appName, year, month, metaName]);

  // (2) Borda isUploading.
  useEffect(() => {
    const wasUploading = prevUploadingRef.current;
    prevUploadingRef.current = isUploading;
    if (!canFetch) return undefined;

    if (!wasUploading && isUploading) {
      deliveredBeforeUploadRef.current =
        controlRef.current?.delivered_at || null;
      // Borda FALSE -> TRUE (reupload OU reprocessamento). Limpamos
      // todo artefato visual ancorado no run anterior — notes (alertas
      // do parser), crossLinks (matriz de cruzamentos), decisions
      // (decisoes pendentes), runtimeInput (estado bruto do input) —
      // para que a UI pare imediatamente de mostrar dados de um run
      // que o usuario acabou de invalidar. O backend tambem aplica
      // essa cascade (``invalidate_dependents`` no clique de reprocess),
      // mas a UI sentia o gap de ~200ms-5s ate o snapshot refazer;
      // sem esta limpeza otimista, o NowPane mantinha a tag vermelha
      // "Arquivo rejeitado" e a aba CrossLinks continuava com
      // porcentagens do run anterior.
      // ``control.state="uploaded"`` espelha a transicao que o backend
      // ja fez no clique. ``runtimeInput`` zera (tambem ancorado em
      // estado pre-clique); panes que dependem dele caem em modo
      // ``loading`` ate o proximo fetch chegar.
      setState((prev) => ({
        ...prev,
        notes: [],
        crossLinks: [],
        decisions: [],
        runtimeInput: null,
        control: prev.control
          ? { ...prev.control, state: "uploaded" }
          : { state: "uploaded" },
      }));
      return undefined;
    }
    if (!(wasUploading && !isUploading)) return undefined;

    const deliveredBefore = deliveredBeforeUploadRef.current;
    setState((prev) => ({
      ...prev,
      notes: [],
      crossLinks: [],
      decisions: [],
      runtimeInput: null,
      control: prev.control
        ? { ...prev.control, state: "uploaded" }
        : { state: "uploaded" },
    }));

    let cancelled = false;
    fetchInputDrawerDetailsV3({ appName, year, month, metaName })
      .then((data) => {
        if (cancelled) return;
        const fetched = data?.control || null;
        const fetchedDelivered = fetched?.delivered_at || null;
        const fetchedState = fetched?.state || null;
        const deliveredUnchanged = fetchedDelivered === deliveredBefore;
        const looksStale =
          deliveredUnchanged && TERMINAL_STATES.has(fetchedState);
        if (looksStale) return;
        setState(shapeFromPayload(data));
      })
      .catch(() => {
        // background — polling tenta de novo.
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch, isUploading, appName, year, month, metaName]);

  // (3) Polling em estados nao-terminais.
  const controlStateForPolling = state.control?.state || null;
  useEffect(() => {
    if (!canFetch) return undefined;
    const needsPolling =
      controlStateForPolling === "uploaded" ||
      controlStateForPolling === "processing";
    if (!needsPolling) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await fetchInputDrawerDetailsV3({
          appName,
          year,
          month,
          metaName,
        });
        if (cancelled) return;
        setState(shapeFromPayload(data));
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({ ...prev, error: err }));
      }
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [canFetch, controlStateForPolling, appName, year, month, metaName]);

  const refresh = useCallback(() => {
    if (!canFetch) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    fetchInputDrawerDetailsV3({ appName, year, month, metaName })
      .then((data) => {
        setState(shapeFromPayload(data));
      })
      .catch((err) => {
        setState((prev) => ({ ...prev, loading: false, error: err }));
      });
  }, [canFetch, appName, year, month, metaName]);

  return { ...state, refresh };
}
